import { ActionCreator } from 'redux';
import { Template, TemplateScopeType, TemplateOrder, TemplateType } from '@dev/ee-api/ee-api-models';

import {
    LoadListErrorAction,
    LoadListStartAction,
    LoadListSuccessAction,
    templateDeleteStartAction,
    templateDeleteSuccessAction,
    templateDeleteErrorAction,
    templatePreviewStartAction,
    templatePreviewErrorAction,
    templatePreviewSuccessAction,
    templateUpdateSuccessAction,
    templateUpdateStartAction,
    templateUpdateErrorAction,
    SaveTemplateStartAction,
    SaveTemplateSuccessAction,
    SaveTemplateErrorAction,
    templateArrayDeleteStartAction,
    templateArrayDeleteSuccessAction,
    templateArrayDeleteErrorAction,
    ShareTemplateStartAction,
    ShareTemplateSuccessAction,
    ShareTemplateErrorAction,
    CreateSharedTemplateErrorAction,
    CreateSharedTemplateSuccessAction,
    CreateSharedTemplateStartAction,
    TemplatesActionTypes,
    TemplateListState,
    SetTemplateListNameAction,
} from './types';
import { ApplicationState } from '..';

export interface StartLoadingTemplateParams {
    page: number;
    limit: number;
    templateOrder?: TemplateOrder;
    search?: string;
    type: TemplateScopeType[];
    tags?: string[];
    templateTypes?: TemplateType[];
    isInfinityScroll?: boolean;
}

export const StartLoadingTemplates: ActionCreator<LoadListStartAction> = (
    params: StartLoadingTemplateParams
): LoadListStartAction => ({
    type: TemplatesActionTypes.LOADING_START,
    payload: {
        loadingTemplates: true,
        page: params.page,
        limit: params.limit,
        type: params.type,
        templateOrder: params.templateOrder,
        search: params.search,
        tags: params.tags,
        templateTypes: params.templateTypes,
        isInfinityScroll: params.isInfinityScroll,
    },
});

export const ClearTemplates = () => ({
    type: TemplatesActionTypes.CLEAR_TEMPLATES,
});

export const LoadingTemplatesSuccess: ActionCreator<LoadListSuccessAction> = (
    list: Template[],
    count: number,
    hasMore?: boolean,
): LoadListSuccessAction => ({
    type: TemplatesActionTypes.LOADING_SUCCESS,
    payload: {
        loadingTemplates: false,
        templatesList: list,
        count,
        hasMore,
    },
});

export const LoadingTemplatesError: ActionCreator<LoadListErrorAction> = (): LoadListErrorAction => ({
    type: TemplatesActionTypes.LOADING_ERROR,
    payload: {
        loadingTemplates: false,
    },
});

export const templateArrayDeleteStart: ActionCreator<templateArrayDeleteStartAction> = (
    templatesToDelete: string[],
    params: StartLoadingTemplateParams
): templateArrayDeleteStartAction => ({
    type: TemplatesActionTypes.DELETE_ARRAY_START,
    payload: {
        templatesToDelete,
        DeletingTemplate: true,
        page: params.page,
        limit: params.limit,
        type: params.type,
        templateOrder: params.templateOrder,
        search: params.search,
        tags: params.tags,
        templateTypes: params.templateTypes,
        loadingTemplates: true,
    },
});
export const templateArrayDeleteSuccess: ActionCreator<templateArrayDeleteSuccessAction> = (
    list: Template[],
    count: number,
    params: StartLoadingTemplateParams
): templateArrayDeleteSuccessAction => ({
    type: TemplatesActionTypes.DELETE_ARRAY_SUCCESS,
    payload: {
        DeletingTemplate: false,
        templatesList: list,
        count,
        page: params.page,
        limit: params.limit,
        type: params.type,
        templateOrder: params.templateOrder,
        search: params.search,
        tags: params.tags,
        templateTypes: params.templateTypes,
        loadingTemplates: false,
    },
});
export const templateArrayDeleteError: ActionCreator<
    templateArrayDeleteErrorAction
> = (): templateArrayDeleteErrorAction => ({
    type: TemplatesActionTypes.DELETE_ARRAY_ERROR,
    payload: {
        DeletingTemplate: false,
        loadingTemplates: false,
    },
});
export const templateDeleteStart: ActionCreator<templateDeleteStartAction> = (
    name: string,
    params: StartLoadingTemplateParams
): templateDeleteStartAction => ({
    type: TemplatesActionTypes.DELETE_START,
    payload: {
        name,
        DeletingTemplate: true,
        loadingTemplates: true,
        page: params.page,
        limit: params.limit,
        type: params.type,
        templateOrder: params.templateOrder,
        search: params.search,
        tags: params.tags,
        templateTypes: params.templateTypes,
    },
});
export const templateDeleteSuccess: ActionCreator<templateDeleteSuccessAction> = (
    name: string,
    list: Template[],
    count: number,
    params: StartLoadingTemplateParams
): templateDeleteSuccessAction => ({
    type: TemplatesActionTypes.DELETE_SUCCESS,
    payload: {
        name,
        DeletingTemplate: false,
        templatesList: list,
        count,
        loadingTemplates: true,
        page: params.page,
        limit: params.limit,
        type: params.type,
        templateOrder: params.templateOrder,
        search: params.search,
        tags: params.tags,
        templateTypes: params.templateTypes,
    },
});
export const templateDeleteError: ActionCreator<templateDeleteErrorAction> = (): templateDeleteErrorAction => ({
    type: TemplatesActionTypes.DELETE_ERROR,
    payload: {
        DeletingTemplate: false,
    },
});
export const TemplatePreviewStart: ActionCreator<templatePreviewStartAction> = (
    name: string,
    ispublic?: boolean
): templatePreviewStartAction => ({
    type: TemplatesActionTypes.PREVIEW_START,
    payload: {
        name,
        ispublic,
        PreviewLoading: true,
    },
});
export const templatePreviewSuccess: ActionCreator<templatePreviewSuccessAction> = (
    name: string,
    template: Template,
    ispublic?: boolean
): templatePreviewSuccessAction => ({
    type: TemplatesActionTypes.PREVIEW_SUCCESS,
    payload: {
        name,
        template,
        ispublic,
        PreviewLoading: false,
    },
});
export const templatePreviewError: ActionCreator<templatePreviewErrorAction> = (): templatePreviewErrorAction => ({
    type: TemplatesActionTypes.PREVIEW_ERROR,
    payload: {
        PreviewLoading: false,
    },
});
export interface StartUpdatingTemplateParams {
    templateName: string;
    template: Template;
    removeScreenshot: boolean;
}
export const templateUpdateStart: ActionCreator<templateUpdateStartAction> = (
    params: StartUpdatingTemplateParams
): templateUpdateStartAction => ({
    type: TemplatesActionTypes.UPDATE_START,
    payload: {
        name: params.templateName,
        template: params.template,
        removeScreenshot: params.removeScreenshot,
    },
});
export const templateUpdateSuccess: ActionCreator<templateUpdateSuccessAction> = (
    name: string,
    list: Template[]
): templateUpdateSuccessAction => ({
    type: TemplatesActionTypes.UPDATE_SUCCESS,
    payload: {
        name,
        UpdatingTemplate: false,
        templatesList: list,
    },
});
export const templateUpdateError: ActionCreator<templateUpdateErrorAction> = (): templateUpdateErrorAction => ({
    type: TemplatesActionTypes.UPDATE_ERROR,
    payload: {
        UpdatingTemplate: false,
    },
});
export const AddTemplateStart: ActionCreator<SaveTemplateStartAction> = (
    template: Template,
    prevTemplateName?: string,
    changes?: Template
): SaveTemplateStartAction => ({
    type: TemplatesActionTypes.SAVE_TEMPLATE_START,
    payload: {
        templateSaving: true,
        template,
        prevTemplateName,
        changes,
    },
});

export const AddTemplateSuccess: ActionCreator<SaveTemplateSuccessAction> = (
    templateObject: Template,
    list: Template[],
    count: number
): SaveTemplateSuccessAction => ({
    type: TemplatesActionTypes.SAVE_TEMPLATE_SUCCESS,
    payload: {
        templateObject,
        templateSaving: false,
        templatesList: list,
        count,
    },
});

export const AddTemplateError: ActionCreator<SaveTemplateErrorAction> = (): SaveTemplateErrorAction => ({
    type: TemplatesActionTypes.SAVE_TEMPLATE_ERROR,
    payload: {
        templateSaving: false,
    },
});

export const ShareTemplateStart = (name: string): ShareTemplateStartAction => ({
    type: TemplatesActionTypes.SHARE_TEMPLATE_START,
    payload: {
        name,
    },
});

export const ShareTemplateSuccess = (shareIdentifier: string): ShareTemplateSuccessAction => ({
    type: TemplatesActionTypes.SHARE_TEMPLATE_SUCCESS,
    payload: {
        shareIdentifier,
    },
});

export const ShareTemplateError = (): ShareTemplateErrorAction => ({
    type: TemplatesActionTypes.SHARE_TEMPLATE_ERROR,
});

export const CreateSharedTemplateStart = (shareIdentifier: string): CreateSharedTemplateStartAction => ({
    type: TemplatesActionTypes.CREATE_SHARED_TEMPLATE_START,
    payload: {
        shareIdentifier,
    },
});

export const createSharedTemplateSuccess = (
    template: Template,
    params: StartLoadingTemplateParams
): CreateSharedTemplateSuccessAction => ({
    type: TemplatesActionTypes.CREATE_SHARED_TEMPLATE_SUCCESS,
    payload: {
        template,
        loadingTemplates: true,
        page: params.page,
        limit: params.limit,
        type: params.type,
        templateOrder: params.templateOrder,
        search: params.search,
        tags: params.tags,
        templateTypes: params.templateTypes,
    },
});

export const createSharedTemplateError = (): CreateSharedTemplateErrorAction => ({
    type: TemplatesActionTypes.CREATE_SHARED_TEMPLATE_ERROR,
});

export const SetTemplateListName = (name: string): SetTemplateListNameAction => ({
    type: TemplatesActionTypes.SET_TEMPLATE_LIST_NAME,
    payload: {
        name,
    },
});

export const getTemplateList = (store: ApplicationState): TemplateListState => store?.templateList;
